import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSyncLogsInfo, SyncLogsInfo, PaginatedResponseLogsInfo } from '../services/syncService';
import './LogsInfoPage.css';

const LogsInfoPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [logsInfo, setLogsInfo] = useState<SyncLogsInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(50);
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [filterText, setFilterText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const isFetchingLogs = useRef(false);

  // Fetch logs from API
  useEffect(() => {
    if (isFetchingLogs.current) return;

    const fetchLogsInfo = async () => {
      try {
        isFetchingLogs.current = true;
        setLoading(true);
        setError(null);
        
        if (!id) {
          setError('No sync ID provided');
          setLoading(false);
          return;
        }

        // Use the sync service which handles authentication automatically
        const response: PaginatedResponseLogsInfo<SyncLogsInfo> = await getSyncLogsInfo(id, currentPage, itemsPerPage, filterText, statusFilter);

        setLogsInfo(response.data);
        setTotalCount(response.total);
        setTotalPages(response.totalPages);
        setIsFiltered(!!filterText.trim());
      } catch (err) {
        console.error('Error fetching logs:', err);
        setError('Failed to load sync logs. Please try again.');
      } finally {
        setLoading(false);
        isFetchingLogs.current = false;
      }
    };

    fetchLogsInfo();
  }, [id, currentPage, itemsPerPage, filterText, statusFilter]);

  const handleBack = () => {
    navigate('/sync-logs');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (limit: number) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to first page when changing limit
  };

  const handleFilterChange = (text: string) => {
    setFilterText(text);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilter = () => {
    setFilterText('');
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="pagination-btn"
      >
        ← Previous
      </button>
    );

    // First page
    if (startPage > 1) {
      buttons.push(
        <button
          key="1"
          onClick={() => handlePageChange(1)}
          className="pagination-btn"
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="pagination-btn"
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="pagination-btn"
      >
        Next →
      </button>
    );

    return buttons;
  };

  if (loading) {
    return (
      <div className="sync-logs-info-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading sync logs information...</p>
        </div>
      </div>
    );
  }

  if (error || !logsInfo) {
    return (
      <div className="sync-logs-info-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error || 'Logs information not found'}</p>
          <button onClick={handleBack} className="back-btn">Back to Logs</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sync-logs-info-page">
      <div className="page-header">
        <div className="header-content">
          <button onClick={handleBack} className="back-btn">
            ← Back to Logs
          </button>
          <div className="sync-date">{logsInfo.created_at}</div>
          <h1>Sync ID: {logsInfo.id}</h1>
        </div>
      </div>

      <div className="sync-controls">
        <div className="filters-section">
          <div className="filter-group">
            <label htmlFor="filterInput">Filter:</label>
            <div className="filter-input-container">
              <input
                id="filterInput"
                type="text"
                placeholder="Search by contract number, description, owner, or comment..."
                value={filterText}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="filter-input"
              />
              {filterText && (
                <button
                  onClick={clearFilter}
                  className="clear-filter-btn"
                  title="Clear filter"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="status-filter">
          <label htmlFor="statusFilter">Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="status-select"
          >
            <option value="">All Statuses</option>
            <option value="created">Created</option>
            <option value="updated">Updated</option>
            <option value="skipped">Skipped</option>
            <option value="unrecognized">Unrecognized</option>
          </select>
        </div>

        <div className="limit-selector">
          <label htmlFor="itemsPerPage">Show:</label>
          <select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="limit-select"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>
      </div>
      
      <div className="sync-items-table">
        <table className="sync-table">
          <thead>
            <tr>
              <th>Created Date</th>
              <th>Contract Number</th>
              <th>Contract Description</th>
              <th>Amount</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Comment</th>
            </tr>
          </thead>
          <tbody>
            {(() => {                  
              if (logsInfo?.syncItems.length > 0) {
                return logsInfo?.syncItems.map((item: any) => (
                  <tr key={item.id}>
                    <td>{item.created_at || 'N/A'}</td>
                    <td>{item.contract.document_number || 'N/A'}</td>
                    <td>{item.contract.name || 'N/A'}</td>
                    <td>{item.contract.amount || 'N/A'}</td>
                    <td>{item.contract.contragent_name || 'N/A'}</td>
                    <td className={`status-${item.status.toLowerCase()}`}>{item.status}</td>
                    <td>{item.comment}</td>
                  </tr>
                ));
              } else {
                return (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                      {isFiltered ? 'No items match the current filters' : 'No sync items found'}
                    </td>
                  </tr>
                );
              }
            })()}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination-info">
          <p>
            Showing {logsInfo?.syncItems.length} of {totalCount} logs
          </p>
        </div>

        {totalPages > 1 && (
          <div className="pagination-controls">
            {renderPaginationButtons()}
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsInfoPage;
